import { Injectable, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION,
  FirebaseEvent,
  FirebaseService,
} from './firebase.service';
import { AppNotification, NotificationsService } from './notifications.service';

export type RealtimeToastKind = 'notification' | 'email' | 'chat';

export interface RealtimeToastItem {
  id: string;
  kind: RealtimeToastKind;
  title: string;
  message: string;
  createdAt: Date;
  route: string | null;
  notification?: AppNotification;
}

const CHAT_NOTIFICATION = 'user:chat.message.received';
const TOAST_LIFETIME_MS = 8000;
const MAX_VISIBLE_TOASTS = 4;

@Injectable({
  providedIn: 'root',
})
export class RealtimeToast implements OnDestroy {
  readonly toasts = signal<RealtimeToastItem[]>([]);

  private readonly firebaseSubscription: Subscription;
  private readonly recentEvents = new Map<string, number>();

  constructor(
    private readonly firebase: FirebaseService,
    private readonly notifications: NotificationsService,
    private readonly router: Router,
  ) {
    this.firebaseSubscription = this.firebase.events$.subscribe((event) => {
      void this.handleEvent(event).catch(() => undefined);
    });
  }

  ngOnDestroy(): void {
    this.firebaseSubscription.unsubscribe();
  }

  dismiss(id: string): void {
    this.toasts.update((items) => items.filter((item) => item.id !== id));
  }

  async open(toast: RealtimeToastItem): Promise<void> {
    this.dismiss(toast.id);
    if (toast.notification) {
      await this.notifications.open(toast.notification);
      return;
    }
    if (toast.route) await this.router.navigateByUrl(toast.route);
  }

  private async handleEvent(event: FirebaseEvent): Promise<void> {
    const kind = this.kindFor(event);
    const deduplicationKey = [
      kind,
      event.notification_id || '',
      event.resource_id || '',
      event.conversation_id || '',
    ].join(':');
    const now = Date.now();
    if (now - (this.recentEvents.get(deduplicationKey) || 0) < 2000) return;
    this.recentEvents.set(deduplicationKey, now);

    if (kind === 'chat') {
      this.show({
        kind,
        title: event.title || `Message from ${event.sender_name || 'a colleague'}`,
        message: event.body || event.message_preview || 'You have a new chat message.',
        route: event.route || (event.conversation_id
          ? `/personal/chat/${event.conversation_id}`
          : '/personal/chat'),
      });
      return;
    }

    if (kind === 'email') {
      const route = event.resource_id
        ? `/mail/${event.resource_id}${event.account_id ? `?account_id=${encodeURIComponent(event.account_id)}` : ''}`
        : '/mail';
      this.show({
        kind,
        title: event.title || 'New email',
        message: event.body || 'A new message arrived in your inbox.',
        route,
      });
      return;
    }

    try {
      await this.notifications.refresh();
    } catch {
      this.show({
        kind,
        title: event.title || 'New notification',
        message: event.body || 'You have a new notification.',
        route: event.route || '/personal/notifications',
      });
      return;
    }
    const snapshot = this.notifications.getSnapshot();
    const notification = snapshot.find((item) =>
      item.notificationId === event.notification_id
      && (!event.resource_id || item.resourceId === event.resource_id)
    ) || snapshot[0];
    if (!notification) return;
    this.show({
      kind,
      title: this.notifications.titleFor(notification),
      message: this.notifications.messageFor(notification),
      route: this.notifications.routeFor(notification),
      notification,
    });
  }

  private show(input: Omit<RealtimeToastItem, 'id' | 'createdAt'>): void {
    const toast: RealtimeToastItem = {
      ...input,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      createdAt: new Date(),
    };
    this.toasts.update((items) => [toast, ...items].slice(0, MAX_VISIBLE_TOASTS));
    window.setTimeout(() => this.dismiss(toast.id), TOAST_LIFETIME_MS);
  }

  private kindFor(event: FirebaseEvent): RealtimeToastKind {
    if (event.kind === 'chat' || event.event === 'chat.updated' || event.notification_id === CHAT_NOTIFICATION) {
      return 'chat';
    }
    if (event.notification_id === EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION) return 'email';
    return 'notification';
  }
}
